import { useState } from 'react';

import { api, type Me } from '../lib/api';
import { Alert, Badge, Card, PageHead } from '../components/ui';
import { Team } from './Team';

export function Settings({ me, onChanged }: { me: Me; onChanged: () => Promise<void> }) {
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <>
      <PageHead title="Settings" subtitle="Account, team and security." />

      {notice && <Alert kind="info">{notice}</Alert>}

      <Card title="Organization">
        <Row label="Name" value={me.tenant.name} />
        <Row label="Tenant ID" value={me.tenant.id} mono />
        <Row label="Slug" value={me.tenant.slug} mono />
        <Row label="Sending domain" value={me.tenant.sending_domain} mono />
        <Row label="Mail type" value={me.tenant.mail_type} />
        <Row
          label="Status"
          value={
            <Badge kind={me.tenant.status === 'active' ? 'ok' : 'warn'}>
              {me.tenant.status}
            </Badge>
          }
        />
        <Row label="Reputation tier" value={me.tenant.reputation_tier} />
      </Card>

      <Team currentUserId={me.user_id} />

      <Card title="Security">
        <p className="subtitle">
          Your session is an HttpOnly, SameSite=Strict cookie, so it cannot be read by
          JavaScript and is not attached to cross-site requests. Writes additionally
          require a header a cross-origin form cannot set.
        </p>
        <div className="row" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-danger"
            onClick={async () => {
              if (
                !window.confirm(
                  'Sign out of every device and browser? You will need to sign in again.',
                )
              ) {
                return;
              }
              // Revokes every session for this user, then drops this one too --
              // what you reach for after a password change or a suspected leak.
              await fetch('/v1/auth/logout-everywhere', {
                method: 'POST',
                headers: { 'X-Mailgiri-Dashboard': '1' },
                credentials: 'same-origin',
              });
              await onChanged();
            }}
          >
            Sign out everywhere
          </button>
          <button
            type="button"
            className="btn"
            onClick={async () => {
              await api.auth.logout();
              await onChanged();
            }}
          >
            Sign out this device
          </button>
        </div>
      </Card>

      <Card title="Not built yet">
        <ul className="subtitle" style={{ paddingLeft: 20, lineHeight: 1.85 }}>
          <li>
            <strong>SSO (SAML/OIDC)</strong> — Phase 6, with org-level enforcement
          </li>
          <li>
            <strong>Full per-role permissions</strong> — Phase 6. Invites work now,
            and inviting or removing people is limited to owners and admins, but every
            other action is still open to any member of the org.
          </li>
          <li>
            <strong>Dedicated IP pools</strong> — Phase 6, gated on plan and volume
          </li>
          <li>
            <strong>Password change and reset</strong> — needs transactional email from
            the platform to itself, which is a nice dogfooding milestone
          </li>
          <li>
            <strong>Notification preferences</strong> — depends on the Phase 6 alerting
            work having something to notify about
          </li>
        </ul>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setNotice('Nothing to configure here yet — see the list above.')}
        >
          Why is this empty?
        </button>
      </Card>
    </>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      className="row"
      style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}
    >
      <span style={{ width: 170, color: 'var(--text-muted)', fontSize: 12.5 }}>{label}</span>
      <span className={mono ? 'mono' : undefined}>{value}</span>
    </div>
  );
}
